import type { FC, ReactNode } from 'react';

import { Modal } from '@zen/DesignSystem';
import type { Undefinable } from '@zen/utils/typescript';

import type { CreateEditModalConfiguration, CreateEditModalType, CreateFormProps, EditFormProps } from './types';

interface Props<InitialValueType, AdditionalCreateFormPropsType, AdditionalEditFormPropsType> {
  additionalCreateFormProps?: AdditionalCreateFormPropsType;
  additionalEditFormProps?: AdditionalEditFormPropsType;
  configuration?: CreateEditModalConfiguration<InitialValueType>;
  createForm: FC<CreateFormProps & AdditionalCreateFormPropsType>;
  editForm: FC<EditFormProps<InitialValueType> & AdditionalEditFormPropsType>;
  onClose: () => void;
}

const CreateEditModal = <
  InitialValueType extends {},
  AdditionalCreateFormPropsType extends {} = {},
  AdditionalEditFormPropsType extends {} = {}
>(
  props: Props<InitialValueType, AdditionalCreateFormPropsType, AdditionalEditFormPropsType>
) => {
  const {
    additionalCreateFormProps,
    additionalEditFormProps,
    configuration,
    onClose,
    editForm: EditForm,
    createForm: CreateForm
  } = props;
  const type: Undefinable<CreateEditModalType> = configuration?.type;
  const initialValues: Undefinable<InitialValueType> = configuration?.initialValues;
  const modalTitle: string = configuration?.title || '';
  const modalOverflowY = configuration?.modalOverflowY || 'scroll';

  const isEdit: boolean = type === 'edit';

  const renderForm = (): ReactNode => {
    if (isEdit && initialValues) {
      return <EditForm item={initialValues} onClose={onClose} {...(additionalEditFormProps as AdditionalEditFormPropsType)} />;
    }

    return <CreateForm onClose={onClose} {...(additionalCreateFormProps as AdditionalCreateFormPropsType)} />;
  };

  return (
    <Modal
      closeOnClickAway={false}
      isOpen={!!configuration}
      modalOverflowY={modalOverflowY}
      onClose={onClose}
      tagline={configuration?.tagline}
      title={modalTitle}
    >
      <>{renderForm()}</>
    </Modal>
  );
};

export default CreateEditModal;
